import React from "react";
import ReactDOM from "react-dom/client";

import { AppErrorBoundary } from "@/components/_organisms/app-error-boundary";
import { AuthProvider } from "@/domain/_shared/auth/hooks/api/use-auth";
import { reloadForStaleChunkWithCooldown } from "@/lib/stale-chunk";
import { AppRouter } from "@/routes/app-router";
import { PrintRouter } from "@/routes/print-router";
import { bootstrapPrintToken } from "@/routes/print-token-bootstrap";
import { PublicEmbedRouter } from "@/routes/public-embed-router";
import { ReactQueryProvider } from "@/services/react-query.service/react-query-provider";

import { initFaro } from "./services/faro.service";
import { trackEvent } from "./services/posthog.service";

import "inter-ui/inter.css";
import "./index.css";

import "./lib/decimal-config";
import "./lib/i18n";

window.addEventListener("vite:preloadError", (event) => {
  if (reloadForStaleChunkWithCooldown()) {
    event.preventDefault();
  }
});

const isEmbedRoute =
  window.location.pathname.startsWith("/embed/") ||
  window.location.hostname === "klimaportal.eu" ||
  window.location.hostname.endsWith(".klimaportal.eu");

const isPrintRoute = window.location.pathname.startsWith("/print/");

if (isPrintRoute) {
  bootstrapPrintToken();
}

// Faro captures errors, web vitals, and user events for Grafana. Print routes
// are headless Puppeteer captures, so monitoring them only produces noise.
// Fire-and-forget: initFaro dynamically imports the SDK, so it must not block
// first render.
if (!isPrintRoute) {
  void initFaro();
}

// Embed and print routes show no cookie-consent banner, so analytics must not fire there.
if (!isEmbedRoute && !isPrintRoute) {
  trackEvent("APP_LOADED");
}

function renderRootRouter() {
  if (isPrintRoute) {
    return <PrintRouter />;
  }
  if (isEmbedRoute) {
    return <PublicEmbedRouter />;
  }
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <ReactQueryProvider>{renderRootRouter()}</ReactQueryProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
